import { type ExWindow } from '@ve/types';
import { computed, getCurrentInstance, unref } from 'vue';

declare let window: ExWindow;
import { useGetters, useState, useActions } from 'vuex-composition-helpers';
import { nameFilterSerpStore } from '@ve/components/searchFilters/store/getFiltersSerpStore';
import { type IMapForFiltersSerpState, type IMapForFiltersSerpActions } from '@ve/components/searchFilters/store/types';
import { type IMapForSearchDataGetters, type IMapForSearchDataState } from '@ve/pages/serp/store';
import { NAMESPACE as MAP_FOR_SEARCH_DATA } from '@ve/pages/serp/store/SearchDataStore';
import { initFilterBudgetBackboneVuex } from '@features/filterByBudget';
import { filterTourProductsByHotels } from '@features/tourProduct/filterTourProducts';
import { useSearchTourProducts } from '@features/tourProduct/searchTourProducts';
import { groupTourProductsByHotel, getUniqHotelsFromGroup, getGroupedPrepayments, getGroupedOperators } from '@entities/tourProduct';
import { getPrepaymentsList } from '@features/firstPaymentDefinition/filterByPrepayments';
import initConnectionBackboneVuex from './initConnectionBackboneVuex';

export default function () {
    initFilterBudgetBackboneVuex();
    initConnectionBackboneVuex();
    const proxy = getCurrentInstance()?.proxy;
    const {
        searchedTourProducts,
        hasTourProductsWithBonusMiles,
        hasTourProductsWithMirCashback,
    } = useSearchTourProducts();
    const {
        isPopularityFilterActive,
        filteredHotelsWithoutOperator,
        filteredHotelsWithoutRestType: filteredHotelsWithoutRestTypeBackbone,
        filteredHotels: filteredHotelsBackbone,
        filteredHotelsWithoutPrice: filteredHotelsWithoutPriceBackbone,
        filteredHotelsWithoutBeachType: filteredHotelsWithoutBeachTypeBacbkone,
        filteredHotelsWithoutRenovations,
        filteredHotelsWithoutPrepayment: filteredHotelsWithoutPrepaymentBackbone,
        beforeToursLoading,
        operators,
    } = useState<IMapForFiltersSerpState>(nameFilterSerpStore, [
        'syncHotels',
        'isPopularityFilterActive',
        'filteredHotelsWithoutOperator',
        'filteredHotels',
        'filteredHotelsWithoutRestType',
        'filteredHotelsWithoutPrice',
        'filteredHotelsWithoutBeachType',
        'filteredHotelsWithoutRenovations',
        'filteredHotelsWithoutPrepayment',
        'beforeToursLoading',
        'operators',
    ]);

    const { touristGroup } = useState<IMapForFiltersSerpState>(nameFilterSerpStore, ['touristGroup']);

    const groupedTourProductsWithoutOperators = computed(() => {
        const filteredTourProductsWithoutOperatorsByHotels = filterTourProductsByHotels(
            unref(searchedTourProducts),
            unref(filteredHotelsWithoutOperator),
        );
        return groupTourProductsByHotel(filteredTourProductsWithoutOperatorsByHotels);
    });

    const groupedTourProductsWithoutPrepayments = computed(() => {
        const filteredTourProductsWithoutPrepaymentsByHotels = filterTourProductsByHotels(unref(searchedTourProducts), unref(filteredHotelsWithoutPrepaymentBackbone));
        return groupTourProductsByHotel(filteredTourProductsWithoutPrepaymentsByHotels);
    });

    const groupedFilteredTourProducts = computed(() => {
        const filteredTourProductsByHotels = filterTourProductsByHotels(
            unref(searchedTourProducts),
            unref(filteredHotelsBackbone),
        );
        return groupTourProductsByHotel(filteredTourProductsByHotels);
    });
    const filteredHotelsWithoutRestType = computed(() => {
        const filteredTourProductsWithoutRestType = filterTourProductsByHotels(
            unref(searchedTourProducts),
            unref(filteredHotelsWithoutRestTypeBackbone),
        );
        const groupedTourProducts = groupTourProductsByHotel(filteredTourProductsWithoutRestType);
        return getUniqHotelsFromGroup(groupedTourProducts);
    });

    const filteredHotelsWithoutBeachType = computed(() => {
        const filteredTourProductsWithoutRestType = filterTourProductsByHotels(
            unref(searchedTourProducts),
            unref(filteredHotelsWithoutBeachTypeBacbkone),
        );
        const groupedTourProducts = groupTourProductsByHotel(filteredTourProductsWithoutRestType);
        return getUniqHotelsFromGroup(groupedTourProducts);
    });

    const tourProductsPrices = computed(() => {
        return unref(filteredHotelsWithoutPriceBackbone).map((hotel) => hotel.minPrice);
    });

    const groupedAllTourProducts = computed(() => groupTourProductsByHotel(unref(searchedTourProducts)));
    const filteredHotels = computed(() => getUniqHotelsFromGroup(unref(groupedFilteredTourProducts)));
    const allHotels = computed(() => getUniqHotelsFromGroup(unref(groupedAllTourProducts)));

    const groupedAllPrepayments = computed(() => getGroupedPrepayments(unref(groupedAllTourProducts)));
    const prepaymentsList = computed(() => getPrepaymentsList(unref(searchedTourProducts)));
    const groupedFilteredPrepayments = computed(() => getGroupedPrepayments(unref(groupedTourProductsWithoutPrepayments)));
    const { directFlight, block, bonusMiles, mirCashback } = useState<IMapForFiltersSerpState>(nameFilterSerpStore, [
        'directFlight',
        'block',
        'bonusMiles',
        'mirCashback',
    ]);

    const { searchCountries, searchDepartureCity } = useState<IMapForSearchDataState>(MAP_FOR_SEARCH_DATA, [
        'searchCountries',
        'searchDepartureCity',
    ]);

    const { isPairOfDepartureCityAndArrivalCountryAreValid } = useGetters<IMapForSearchDataGetters>(
        MAP_FOR_SEARCH_DATA,
        ['isPairOfDepartureCityAndArrivalCountryAreValid'],
    );

    const directFlightModel = computed({
        get: () => directFlight.value,
        set: (value) => {
            proxy?.$root.$emit('start-search-by-direct-flight-filter', value);
        },
    });

    const blockModel = computed({
        get: () => block.value,
        set: (value) => {
            proxy?.$root.$emit('block-changed', value);
        },
    });

    const bonusMilesModel = computed({
        get: () => bonusMiles.value ?? false,
        set: (value) => {
            proxy?.$root.$emit('bonus-miles-changed', value);
        },
    });

    const isBonusMilesFilterVisible = computed<boolean>(() => !unref(beforeToursLoading) && unref(hasTourProductsWithBonusMiles));

    const groupedAllOperators = computed(() => getGroupedOperators(unref(groupedAllTourProducts)));

    const groupedFilteredOperators = computed(() => getGroupedOperators(unref(groupedTourProductsWithoutOperators)));

    const mirCashbackModel = computed({
        get: () => mirCashback.value ?? false,
        set: (value) => {
            proxy?.$root.$emit('mir-cashback-changed', value);
        },
    });

    const isMirCashbackFilterVisible = computed<boolean>(
        () => !!window?.appConfig?.vamPrivet && !unref(beforeToursLoading) && unref(hasTourProductsWithMirCashback),
    );
    return {
        directFlightModel,
        touristGroup,
        groupedAllTourProducts,
        groupedTourProductsWithoutOperators,
        groupedAllOperators,
        groupedFilteredOperators,
        filteredHotelsWithoutRestType,
        filteredHotelsWithoutBeachType,
        filteredHotelsWithoutRenovations,
        filteredHotels,
        allHotels,
        groupedFilteredTourProducts,
        tourProductsPrices,
        isPopularityFilterActive,
        searchCountries,
        searchDepartureCity,
        blockModel,
        bonusMilesModel,
        isBonusMilesFilterVisible,
        mirCashbackModel,
        isMirCashbackFilterVisible,
        isPairOfDepartureCityAndArrivalCountryAreValid,
        operators,
        groupedAllPrepayments,
        prepaymentsList,
        groupedFilteredPrepayments,
    };
}
